import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Breadcrumbs,
  Link,
  Chip,
  IconButton,
  CircularProgress,
  Alert,
  alpha,
  TextField,
  InputAdornment,
  Button,
  Stack,
  Divider,
  Avatar,
  Badge,
} from '@mui/material';
import {
  Business,
  People,
  Person,
  NavigateNext,
  AdminPanelSettings,
  Close,
  Search,
  TrendingUp,
  Layers,
  Email,
  CalendarToday,
  Group,
  Star,
  KeyboardArrowRight,
  Storage,
} from '@mui/icons-material';
import { fetchOrganizations, fetchClients, fetchUsers, User } from '../api/admin';
import { Organization, Client } from '../types/admin';
import { useUser } from './UserContext';

const THEME = {
  BG_PRIMARY: '#000000',
  BG_SECONDARY: '#0a0a0a',
  BG_TERTIARY: '#141414',
  BG_CARD: '#1a1a1a',
  TEXT_PRIMARY: '#ffffff',
  TEXT_SECONDARY: '#a0a0a0',
  TEXT_MUTED: '#666666',
  ACCENT_PRIMARY: '#2196f3',
  ACCENT_SUCCESS: '#00e676',
  ACCENT_WARNING: '#ffa726',
  ACCENT_ERROR: '#ff5252',
  ACCENT_PURPLE: '#ab47bc',
  BORDER_SUBTLE: '#252525',
  BORDER_DEFAULT: '#333333',
};

type ViewLevel = 'organizations' | 'clients' | 'users';

interface NavigationState {
  level: ViewLevel;
  selectedOrganization: Organization | null;
  selectedClient: Client | null;
}

export const Dashboard1: React.FC<{ onClose?: () => void }> = ({ onClose }) => {
  const { user: currentUser } = useUser();
  const isSuperAdmin = currentUser?.role === 'super_admin' || currentUser?.role === 'superadmin';
  const isOrgAdmin = currentUser?.role === 'admin';

  const [navigation, setNavigation] = useState<NavigationState>({
    level: isSuperAdmin ? 'organizations' : 'clients',
    selectedOrganization: null,
    selectedClient: null,
  });

  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    // Load initial data depending on role
    (async () => {
      try {
        if (isSuperAdmin) {
          await loadOrganizations();
          // Super admin should see totals across all clients and users
          try {
            const allClients = await fetchClients();
            setClients(allClients || []);
          } catch (err) {
            // Warning('Unable to load all clients for superadmin', err);
          }
          try {
            const allUsers = await fetchUsers();
            setUsers(allUsers || []);
          } catch (err) {
            // Warning('Unable to load all users for superadmin', err);
          }
        } else if (isOrgAdmin && currentUser?.organization_id) {
          await loadClientsForOrganization(currentUser.organization_id);
        } else if (currentUser?.organization_id) {
          // non-admin users: load clients for their organization so counts are meaningful
          await loadClientsForOrganization(currentUser.organization_id);
        }

        // If the current user belongs to a client, load its users (so Total Users reflects that client)
        if (currentUser?.client_id) {
          await loadUsersForClient(currentUser.client_id);
        }
      } catch (e) {
        // Error('Error during initial dashboard load', e);
      }
    })();
  }, []);

  const loadOrganizations = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await fetchOrganizations();
      setOrganizations(data);
    } catch (err: any) {
      setError('Unable to load organizations');
      // Error('Error loading organizations:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadClientsForOrganization = async (orgId: number) => {
    try {
      setLoading(true);
      setError(null);
      const allClients = await fetchClients();
      const filteredClients = allClients.filter(client => client.organization_id === orgId);
      setClients(filteredClients);
    } catch (err: any) {
      setError('Unable to load clients');
      // Error('Error loading clients:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadUsersForClient = async (clientId: number) => {
    try {
      setLoading(true);
      setError(null);
      const allUsers = await fetchUsers();
      // Filter users to only those belonging to the selected client
      const clientUsers = allUsers.filter(u => u.client_id === clientId);
      setUsers(clientUsers);
    } catch (err: any) {
      setError('Unable to load users');
      // Error('Error loading users:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleOrganizationClick = async (org: Organization) => {
    setNavigation({
      level: 'clients',
      selectedOrganization: org,
      selectedClient: null,
    });
    setSearchQuery('');
    await loadClientsForOrganization(org.id);
  };

  const handleClientClick = async (client: Client) => {
    setNavigation(prev => ({
      ...prev,
      level: 'users',
      selectedClient: client,
    }));
    setSearchQuery('');
    await loadUsersForClient(client.id);
  };

  const handleBack = () => {
    if (navigation.level === 'users') {
      setNavigation(prev => ({
        ...prev,
        level: 'clients',
        selectedClient: null,
      }));
      setUsers([]);
    } else if (navigation.level === 'clients') {
      setNavigation({
        level: 'organizations',
        selectedOrganization: null,
        selectedClient: null,
      });
      setClients([]);
      setUsers([]);
    }
    setSearchQuery('');
  };

  const filteredData = () => {
    const query = searchQuery.toLowerCase();
    if (navigation.level === 'organizations') {
      return organizations.filter(org => org.name.toLowerCase().includes(query));
    } else if (navigation.level === 'clients') {
      return clients.filter(client => client.name.toLowerCase().includes(query));
    } else if (navigation.level === 'users') {
      return users.filter(user => 
        (user.full_name?.toLowerCase().includes(query) || '') || 
        user.email.toLowerCase().includes(query) ||
        user.username.toLowerCase().includes(query)
      );
    }
    return [];
  };

  const getRoleColor = (role?: string | null) => {
    if (!role) return THEME.TEXT_MUTED;
    if (role === 'superadmin' || role === 'super_admin') return THEME.ACCENT_PRIMARY;
    if (role === 'admin') return THEME.ACCENT_WARNING;
    return THEME.ACCENT_SUCCESS;
  };

  const getOrgStats = () => {
    const totalClients = organizations.reduce((sum, org) => {
      const orgClients = clients.filter(c => c.organization_id === org.id);
      return sum + orgClients.length;
    }, 0);
    
    return {
      totalOrgs: organizations.length,
      totalClients: clients.length,
      totalUsers: users.length,
    };
  };

  // Map navigation.level to Italian labels for UI
  const levelLabel = navigation.level === 'organizations'
    ? 'organizzazioni'
    : navigation.level === 'clients'
      ? 'clienti'
      : 'utenti';

  return (
    <Box sx={{ 
      minHeight: '100vh', 
      bgcolor: THEME.BG_PRIMARY,
      position: 'relative',
      // allow page scrolling so sticky header and long content work
      overflow: 'visible',
    }}>
      {/* Animated background gradient */}
      <Box sx={{
        position: 'fixed',
        top: '-50%',
        left: '-50%',
        width: '200%',
        height: '200%',
        background: `radial-gradient(circle at 20% 50%, ${alpha(THEME.ACCENT_PRIMARY, 0.03)} 0%, transparent 25%),
                     radial-gradient(circle at 80% 80%, ${alpha(THEME.ACCENT_SUCCESS, 0.03)} 0%, transparent 25%),
                     radial-gradient(circle at 40% 20%, ${alpha(THEME.ACCENT_PURPLE, 0.03)} 0%, transparent 25%)`,
        animation: 'drift 20s ease-in-out infinite',
        '@keyframes drift': {
          '0%, 100%': { transform: 'translate(0, 0)' },
          '50%': { transform: 'translate(50px, 50px)' },
        },
      }} />

      {/* Header */}
      <Box sx={{
        position: 'sticky',
        top: 0,
        zIndex: 100,
        backdropFilter: 'blur(20px)',
        bgcolor: alpha(THEME.BG_SECONDARY, 0.8),
        borderBottom: `1px solid ${THEME.BORDER_SUBTLE}`,
        boxShadow: `0 4px 24px ${alpha('#000', 0.4)}`,
      }}>
        <Box sx={{ px: 4, py: 3 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Stack direction="row" alignItems="center" spacing={3}>
              <Box sx={{
                width: 48,
                height: 48,
                borderRadius: '12px',
                background: `linear-gradient(135deg, ${THEME.ACCENT_PRIMARY}, ${THEME.ACCENT_PURPLE})`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: `0 8px 24px ${alpha(THEME.ACCENT_PRIMARY, 0.4)}`,
              }}>
                <Layers sx={{ color: '#fff', fontSize: 28 }} />
              </Box>
              <Box>
                <Typography variant="h4" sx={{ 
                  color: THEME.TEXT_PRIMARY, 
                  fontWeight: 700,
                  letterSpacing: '-0.02em',
                }}>
                  Cruscotto gerarchico
                </Typography>
                <Typography variant="body2" sx={{ color: THEME.TEXT_SECONDARY, mt: 0.5 }}>
                  Gestisci organizzazioni, clienti e utenti in un unico posto
                </Typography>
              </Box>
            </Stack>
            {/* Always show a close control. If parent passed onClose use it, otherwise fallback to history.back() */}
            <IconButton
              onClick={() => (onClose ? onClose() : window.history.back())}
              sx={{
                bgcolor: alpha(THEME.ACCENT_ERROR, 0.1),
                color: THEME.ACCENT_ERROR,
                '&:hover': {
                  bgcolor: THEME.ACCENT_ERROR,
                  color: '#fff',
                  transform: 'rotate(90deg)',
                },
                transition: 'all 0.3s',
              }}
            >
              <Close />
            </IconButton>
           
          </Stack>
        </Box>

        {/* Breadcrumbs & Search */}
        <Box sx={{ px: 4, pb: 3 }}>
          <Stack direction="row" spacing={3} alignItems="center">
            <Breadcrumbs
              separator={<NavigateNext sx={{ color: THEME.TEXT_MUTED, fontSize: 20 }} />}
              sx={{ flex: 1 }}
            >
              {isSuperAdmin && (
                <Link
                  component="button"
                  onClick={() => navigation.level !== 'organizations' && handleBack()}
                  sx={{
                    color: navigation.level === 'organizations' ? THEME.ACCENT_PRIMARY : THEME.TEXT_SECONDARY,
                    textDecoration: 'none',
                    fontWeight: navigation.level === 'organizations' ? 600 : 400,
                    fontSize: 15,
                    '&:hover': { color: THEME.ACCENT_PRIMARY },
                    cursor: navigation.level === 'organizations' ? 'default' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  <Business sx={{ fontSize: 18 }} />
                  Organizations
                </Link>
              )}
              
              {navigation.selectedOrganization && (
                <Link
                  component="button"
                  onClick={() => navigation.level === 'users' && handleBack()}
                  sx={{
                    color: navigation.level === 'clients' ? THEME.ACCENT_PRIMARY : THEME.TEXT_SECONDARY,
                    textDecoration: 'none',
                    fontWeight: navigation.level === 'clients' ? 600 : 400,
                    fontSize: 15,
                    '&:hover': { color: THEME.ACCENT_PRIMARY },
                    cursor: navigation.level === 'clients' ? 'default' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  <People sx={{ fontSize: 18 }} />
                  {navigation.selectedOrganization.name}
                </Link>
              )}
              
              {navigation.selectedClient && (
                <Typography sx={{ 
                  color: THEME.ACCENT_PRIMARY, 
                  fontWeight: 600,
                  fontSize: 15,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                }}>
                  <Person sx={{ fontSize: 18 }} />
                  {navigation.selectedClient.name}
                </Typography>
              )}
            </Breadcrumbs>

            <TextField
              placeholder={`Cerca ${levelLabel}...`}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              size="small"
              sx={{
                width: 300,
                '& .MuiOutlinedInput-root': {
                  bgcolor: THEME.BG_TERTIARY,
                  color: THEME.TEXT_PRIMARY,
                  borderRadius: 2,
                  '& fieldset': { borderColor: THEME.BORDER_SUBTLE },
                  '&:hover fieldset': { borderColor: THEME.BORDER_DEFAULT },
                  '&.Mui-focused fieldset': { borderColor: THEME.ACCENT_PRIMARY },
                  // placeholder color for outlined input
                  '& .MuiOutlinedInput-input::placeholder': {
                    color: THEME.ACCENT_PRIMARY,
                    opacity: 1,
                  }
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ color: THEME.ACCENT_PRIMARY }} />
                  </InputAdornment>
                ),
              }}
            />
          </Stack>
        </Box>
      </Box>

      {/* Main Content */}
      <Box sx={{ px: 4, py: 4, position: 'relative', zIndex: 1 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 12 }}>
            <CircularProgress size={60} sx={{ color: THEME.ACCENT_PRIMARY }} />
          </Box>
        ) : (
          <>
            {/* Organizations View */}
            {navigation.level === 'organizations' && (
              <Box>
                {/* Stats Cards */}
                <Stack direction="row" spacing={2} sx={{ mb: 4 }}>
                  <Card sx={{
                    flex: 1,
                    bgcolor: alpha(THEME.ACCENT_PRIMARY, 0.08),
                    border: `1px solid ${alpha(THEME.ACCENT_PRIMARY, 0.2)}`,
                    borderRadius: 3,
                  }}>
                    <CardContent>
                      <Stack direction="row" alignItems="center" spacing={2}>
                        <Box sx={{
                          width: 48,
                          height: 48,
                          borderRadius: 2,
                          bgcolor: alpha(THEME.ACCENT_PRIMARY, 0.2),
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}>
                          <Business sx={{ color: THEME.ACCENT_PRIMARY }} />
                        </Box>
                        <Box>
                          <Typography variant="h3" sx={{ color: THEME.TEXT_PRIMARY, fontWeight: 700 }}>
                            {organizations.length}
                          </Typography>
                          <Typography variant="body2" sx={{ color: THEME.TEXT_SECONDARY }}>
                            Totale organizzazioni
                          </Typography>
                        </Box>
                      </Stack>
                    </CardContent>
                  </Card>

                  <Card sx={{
                    flex: 1,
                    bgcolor: alpha(THEME.ACCENT_SUCCESS, 0.08),
                    border: `1px solid ${alpha(THEME.ACCENT_SUCCESS, 0.2)}`,
                    borderRadius: 3,
                  }}>
                    <CardContent>
                      <Stack direction="row" alignItems="center" spacing={2}>
                        <Box sx={{
                          width: 48,
                          height: 48,
                          borderRadius: 2,
                          bgcolor: alpha(THEME.ACCENT_SUCCESS, 0.2),
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}>
                          <TrendingUp sx={{ color: THEME.ACCENT_SUCCESS }} />
                        </Box>
                        <Box>
                          <Typography variant="h3" sx={{ color: THEME.TEXT_PRIMARY, fontWeight: 700 }}>
                            {clients.length}
                          </Typography>
                          <Typography variant="body2" sx={{ color: THEME.TEXT_SECONDARY }}>
                            Totale clienti
                          </Typography>
                        </Box>
                      </Stack>
                    </CardContent>
                  </Card>

                  <Card sx={{
                    flex: 1,
                    bgcolor: alpha(THEME.ACCENT_WARNING, 0.08),
                    border: `1px solid ${alpha(THEME.ACCENT_WARNING, 0.2)}`,
                    borderRadius: 3,
                  }}>
                    <CardContent>
                      <Stack direction="row" alignItems="center" spacing={2}>
                        <Box sx={{
                          width: 48,
                          height: 48,
                          borderRadius: 2,
                          bgcolor: alpha(THEME.ACCENT_WARNING, 0.2),
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}>
                          <Group sx={{ color: THEME.ACCENT_WARNING }} />
                        </Box>
                        <Box>
                          <Typography variant="h3" sx={{ color: THEME.TEXT_PRIMARY, fontWeight: 700 }}>
                            {users.length}
                          </Typography>
                          <Typography variant="body2" sx={{ color: THEME.TEXT_SECONDARY }}>
                            Totale utenti
                          </Typography>
                        </Box>
                      </Stack>
                    </CardContent>
                  </Card>
                </Stack>

                {/* Organizations Grid */}
                <Grid container spacing={3} alignItems="stretch">
                  {(filteredData() as Organization[]).map((org: Organization) => (
                    <Grid item xs={12} sm={6} md={4} key={org.id}>
                      <Card
                        onClick={() => handleOrganizationClick(org)}
                        sx={{
                          bgcolor: THEME.BG_CARD,
                          border: `1px solid ${THEME.BORDER_SUBTLE}`,
                          borderRadius: 3,
                          cursor: 'pointer',
                          position: 'relative',
                          overflow: 'hidden',
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          minHeight: 220,
                          display: 'flex',
                          flexDirection: 'column',
                          '&:hover': {
                            borderColor: THEME.ACCENT_PRIMARY,
                            transform: 'translateY(-8px)',
                            boxShadow: `0 20px 40px ${alpha(THEME.ACCENT_PRIMARY, 0.2)}`,
                            '& .arrow': {
                              transform: 'translateX(4px)',
                            },
                          },
                        }}
                      >
                        <Box sx={{
                          position: 'absolute',
                          top: 0,
                          right: 0,
                          width: 150,
                          height: 150,
                          background: `radial-gradient(circle, ${alpha(THEME.ACCENT_PRIMARY, 0.1)}, transparent)`,
                        }} />
                        <CardContent sx={{ p: 3, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', flex: 1 }}>
                          <Stack direction="row" alignItems="flex-start" spacing={2} sx={{ mb: 2 }}>
                            <Box sx={{
                              width: 56,
                              height: 56,
                              borderRadius: 2.5,
                              background: `linear-gradient(135deg, ${alpha(THEME.ACCENT_PRIMARY, 0.2)}, ${alpha(THEME.ACCENT_PURPLE, 0.2)})`,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              border: `1px solid ${alpha(THEME.ACCENT_PRIMARY, 0.3)}`,
                            }}>
                              <Business sx={{ color: THEME.ACCENT_PRIMARY, fontSize: 28 }} />
                            </Box>
                            <Box sx={{ flex: 1 }}>
                              <Typography variant="h6" sx={{ color: THEME.TEXT_PRIMARY, fontWeight: 600, mb: 0.5 }}>
                                {org.name}
                              </Typography>
                              {org.is_root && (
                                <Chip
                                  label="ROOT"
                                  size="small"
                                  icon={<Star sx={{ fontSize: 14 }} />}
                                  sx={{
                                    bgcolor: alpha(THEME.ACCENT_WARNING, 0.2),
                                    color: THEME.ACCENT_WARNING,
                                    fontWeight: 700,
                                    height: 24,
                                    border: `1px solid ${alpha(THEME.ACCENT_WARNING, 0.3)}`,
                                  }}
                                />
                              )}
                            </Box>
                          </Stack>

                          {org.description && (
                            <Typography variant="body2" sx={{ color: THEME.TEXT_SECONDARY, mb: 3, minHeight: 40 }}>
                              {org.description}
                            </Typography>
                          )}

                          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ pt: 2, borderTop: `1px solid ${THEME.BORDER_SUBTLE}` }}>
                              <Typography variant="caption" sx={{ color: THEME.TEXT_MUTED }}>
                              Clicca per esplorare i clienti
                            </Typography>
                            <KeyboardArrowRight className="arrow" sx={{ color: THEME.ACCENT_PRIMARY, transition: 'transform 0.3s' }} />
                          </Stack>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}

            {/* Clients View */}
            {navigation.level === 'clients' && (
              <Box>
                {clients.length === 0 ? (
                  <Alert severity="info" sx={{ bgcolor: alpha(THEME.ACCENT_PRIMARY, 0.1) }}>
                    Nessun cliente trovato per questa organizzazione
                  </Alert>
                ) : (
                  <Grid container spacing={3} alignItems="stretch">
                    {(filteredData() as Client[]).map((client: Client) => (
                      <Grid item xs={12} sm={6} md={4} key={client.id}>
                        <Card
                          onClick={() => handleClientClick(client)}
                          sx={{
                            bgcolor: THEME.BG_CARD,
                            border: `1px solid ${THEME.BORDER_SUBTLE}`,
                            borderRadius: 3,
                            cursor: 'pointer',
                            position: 'relative',
                            overflow: 'hidden',
                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                            minHeight: 220,
                            display: 'flex',
                            flexDirection: 'column',
                            '&:hover': {
                              borderColor: THEME.ACCENT_SUCCESS,
                              transform: 'translateY(-8px)',
                              boxShadow: `0 20px 40px ${alpha(THEME.ACCENT_SUCCESS, 0.2)}`,
                              '& .arrow': {
                                transform: 'translateX(4px)',
                              },
                            },
                          }}
                        >
                          <Box sx={{
                            position: 'absolute',
                            top: 0,
                            right: 0,
                            width: 150,
                            height: 150,
                            background: `radial-gradient(circle, ${alpha(THEME.ACCENT_SUCCESS, 0.1)}, transparent)`,
                          }} />
                          <CardContent sx={{ p: 3, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', flex: 1 }}>
                            <Stack direction="row" alignItems="flex-start" spacing={2} sx={{ mb: 2 }}>
                              <Box sx={{
                                width: 56,
                                height: 56,
                                borderRadius: 2.5,
                                background: `linear-gradient(135deg, ${alpha(THEME.ACCENT_SUCCESS, 0.2)}, ${alpha(THEME.ACCENT_PRIMARY, 0.2)})`,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                border: `1px solid ${alpha(THEME.ACCENT_SUCCESS, 0.3)}`,
                              }}>
                                <People sx={{ color: THEME.ACCENT_SUCCESS, fontSize: 28 }} />
                              </Box>
                              <Box sx={{ flex: 1 }}>
                                <Typography variant="h6" sx={{ color: THEME.TEXT_PRIMARY, fontWeight: 600, mb: 0.5 }}>
                                  {client.name}
                                </Typography>
                              </Box>
                            </Stack>

                            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ pt: 2, borderTop: `1px solid ${THEME.BORDER_SUBTLE}` }}>
                              <Typography variant="caption" sx={{ color: THEME.TEXT_MUTED }}>
                                Visualizza utenti
                              </Typography>
                              <KeyboardArrowRight className="arrow" sx={{ color: THEME.ACCENT_SUCCESS, transition: 'transform 0.3s' }} />
                            </Stack>
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                )}
              </Box>
            )}

            {/* Users View */}
            {navigation.level === 'users' && (
              <Box>
                {users.length === 0 ? (
                  <Alert severity="info" sx={{ bgcolor: alpha(THEME.ACCENT_PRIMARY, 0.1) }}>
                    Nessun utente trovato per questo cliente
                  </Alert>
                ) : (
                  <Grid container spacing={3} alignItems="stretch">
                    {(filteredData() as User[]).map((user: User) => (
                      <Grid item xs={12} sm={6} md={4} key={user.id}>
                        <Card
                          sx={{
                            bgcolor: THEME.BG_CARD,
                            border: `1px solid ${THEME.BORDER_SUBTLE}`,
                            borderRadius: 3,
                            position: 'relative',
                            overflow: 'hidden',
                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                            minHeight: 220,
                            display: 'flex',
                            flexDirection: 'column',
                            '&:hover': {
                              borderColor: getRoleColor(user.role),
                              transform: 'translateY(-4px)',
                              boxShadow: `0 12px 32px ${alpha(getRoleColor(user.role), 0.2)}`,
                            },
                          }}
                        >
                          <Box sx={{
                            position: 'absolute',
                            top: 0,
                            right: 0,
                            width: 120,
                            height: 120,
                            background: `radial-gradient(circle, ${alpha(getRoleColor(user.role), 0.1)}, transparent)`,
                          }} />
                          <CardContent sx={{ p: 3, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', flex: 1 }}>
                            <Stack direction="row" alignItems="flex-start" spacing={2} sx={{ mb: 2 }}>
                              <Badge
                                overlap="circular"
                                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                                badgeContent={
                                  user.is_active ? (
                                    <Box sx={{
                                      width: 12,
                                      height: 12,
                                      borderRadius: '50%',
                                      bgcolor: THEME.ACCENT_SUCCESS,
                                      border: `2px solid ${THEME.BG_CARD}`,
                                    }} />
                                  ) : null
                                }
                              >
                                <Avatar
                                  sx={{
                                    width: 56,
                                    height: 56,
                                    bgcolor: alpha(getRoleColor(user.role), 0.2),
                                    color: getRoleColor(user.role),
                                    fontWeight: 700,
                                    fontSize: 20,
                                    border: `2px solid ${alpha(getRoleColor(user.role), 0.3)}`,
                                  }}
                                >
                                  {(user.full_name || user.username).split(' ').map(n => n[0]).join('').slice(0, 2)}
                                </Avatar>
                              </Badge>
                              <Box sx={{ flex: 1 }}>
                                <Typography variant="h6" sx={{ color: THEME.TEXT_PRIMARY, fontWeight: 600, mb: 0.5 }}>
                                  {user.full_name || user.username}
                                </Typography>
                                <Stack direction="row" spacing={0.5}>
                                  {(user.role === 'superadmin' || user.role === 'super_admin') && (
                                    <Chip
                                      label="SUPER ADMIN"
                                      size="small"
                                      icon={<AdminPanelSettings sx={{ fontSize: 12 }} />}
                                      sx={{
                                        bgcolor: alpha(THEME.ACCENT_PRIMARY, 0.2),
                                        color: THEME.ACCENT_PRIMARY,
                                        fontWeight: 700,
                                        fontSize: '0.65rem',
                                        height: 22,
                                        border: `1px solid ${alpha(THEME.ACCENT_PRIMARY, 0.3)}`,
                                      }}
                                    />
                                  )}
                                  {user.role === 'admin' && (
                                    <Chip
                                      label="ADMIN"
                                      size="small"
                                      icon={<AdminPanelSettings sx={{ fontSize: 12 }} />}
                                      sx={{
                                        bgcolor: alpha(THEME.ACCENT_WARNING, 0.2),
                                        color: THEME.ACCENT_WARNING,
                                        fontWeight: 700,
                                        fontSize: '0.65rem',
                                        height: 22,
                                        border: `1px solid ${alpha(THEME.ACCENT_WARNING, 0.3)}`,
                                      }}
                                    />
                                  )}
                                </Stack>
                              </Box>
                            </Stack>

                            <Stack spacing={1.5} sx={{ mb: 2 }}>
                              <Stack direction="row" alignItems="center" spacing={1.5}>
                                <Email sx={{ color: THEME.TEXT_MUTED, fontSize: 16 }} />
                                  <Typography variant="body2" sx={{ color: THEME.TEXT_SECONDARY, fontSize: '0.85rem' }}>
                                  {user.email}
                                </Typography>
                              </Stack>
                              <Stack direction="row" alignItems="center" spacing={1.5}>
                                <Person sx={{ color: THEME.TEXT_MUTED, fontSize: 16 }} />
                                <Typography variant="body2" sx={{ color: THEME.TEXT_SECONDARY, fontSize: '0.85rem' }}>
                                  @{user.username}
                                </Typography>
                              </Stack>
                              {user.created_at && (
                                <Stack direction="row" alignItems="center" spacing={1.5}>
                                  <CalendarToday sx={{ color: THEME.TEXT_MUTED, fontSize: 16 }} />
                                  <Typography variant="body2" sx={{ color: THEME.TEXT_SECONDARY, fontSize: '0.85rem' }}>
                                    Iscritto il {new Date(user.created_at).toLocaleDateString()}
                                  </Typography>
                                </Stack>
                              )}
                            </Stack>

                            <Divider sx={{ borderColor: THEME.BORDER_SUBTLE, my: 2 }} />

                            <Stack direction="row" alignItems="center" justifyContent="space-between">
                              <Chip
                                label={user.is_active ? 'Attivo' : 'Non attivo'}
                                size="small"
                                sx={{
                                  bgcolor: user.is_active 
                                    ? alpha(THEME.ACCENT_SUCCESS, 0.2) 
                                    : alpha(THEME.TEXT_MUTED, 0.2),
                                  color: user.is_active ? THEME.ACCENT_SUCCESS : THEME.TEXT_MUTED,
                                  fontWeight: 600,
                                  fontSize: '0.7rem',
                                  height: 24,
                                  border: `1px solid ${user.is_active 
                                    ? alpha(THEME.ACCENT_SUCCESS, 0.3) 
                                    : alpha(THEME.TEXT_MUTED, 0.3)}`,
                                }}
                              />
                              <Stack direction="row" spacing={0.5} alignItems="center">
                                <Storage sx={{ color: THEME.TEXT_MUTED, fontSize: 14 }} />
                                <Typography variant="caption" sx={{ color: THEME.TEXT_MUTED, fontSize: '0.7rem' }}>
                                  ID: {user.id}
                                </Typography>
                              </Stack>
                            </Stack>
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                )}
              </Box>
            )}
          </>
        )}
      </Box>
    </Box>
  );
};

export default Dashboard1;
